import React, { useCallback, useEffect, useRef, useState } from 'react';

import { BarConfig } from '@ant-design/plots';
import { Bar } from '@ant-design/plots/lib';
import styled from 'styled-components';

import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import { INTERVAL_PADDING, MAX_BAR_WIDTH, MIN_BAR_WIDTH } from 'constants/charts';
import { useAppSelector } from 'store/hook';
import { getDataInsertionBars } from 'utils/helpers/antdChart';
import { useClickableAntdBars } from 'utils/hooks/useClickableAntdBars';

import DataInsertionBars from './DataInsertionBars';
import EditDataModal from './EditDataModal';
import { DataInsertionBar } from './types';
import { useDifferenceColorObserver } from './useDifferenceColorObserver';
import { buildYAxisLabel, textStyle } from './utils';
import { WIDGET_TYPES, WIDGETS_WITH_ALLOWED_DATA_INSERTION } from '../constants';
import { shouldDisplayValueDifference } from '../valueDifferenceUtils';
import { useWidgetContext } from '../Widget/WidgetContext';

const Container = styled.div`
  position: relative;
  height: 100%;
`;

export const BarChart = (props: BarConfig) => {
  const chartRef = useRef<any>(null);
  const { type, filters } = useWidgetContext();
  const { community: mainCommunity } = useCommunityViewContext();
  const [addDataBarPositions, setAddDataBarPositions] = useState<DataInsertionBar[]>([]);
  const [showInsertDataModal, setShowInsertDataModal] = useState(false);
  const [selectedCompetitorId, setSelectedCompetitorId] = useState<number | null>(null);
  const feePricingTypes = useAppSelector((state) => state.global.feePricingTypes ?? []);
  const livingTypes = useAppSelector((state) => state.global.livingTypes ?? []);
  const showValueDifference = shouldDisplayValueDifference(filters.comparisonTypeEnabled, filters.valueDisplay);
  const { onChartReady } = useDifferenceColorObserver(showValueDifference, type);

  const closeModal = useCallback(() => setShowInsertDataModal(false), [setShowInsertDataModal]);

  const showCareType = [
    WIDGET_TYPES.CARE_FEES_GRAPH,
    WIDGET_TYPES.MEDICATION_FEES_GRAPH,
    WIDGET_TYPES.MARKET_RATE_AVERAGE_CARE_COST_GRAPH,
    WIDGET_TYPES.MARKET_RATE_CARE_MEDICATION_COST_GRAPH,
    WIDGET_TYPES.COMMUNITY_FEES_GRAPH,
  ].includes(type);
  const showLevelsCount = [WIDGET_TYPES.CARE_FEES_GRAPH, WIDGET_TYPES.MEDICATION_FEES_GRAPH].includes(type);

  const onReady = (plot: any) => {
    onChartReady(plot);

    if (!WIDGETS_WITH_ALLOWED_DATA_INSERTION.includes(type)) {
      return;
    }
    const positions = getDataInsertionBars(plot, props);
    setAddDataBarPositions(positions);
  };

  const yAxisLabelFormatter = useCallback(
    (text: string, item: any, index: number) =>
      buildYAxisLabel({
        text,
        item,
        index,
        data: props.data,
        type,
        livingTypes,
        feePricingTypes,
        showLevelsCount,
      }),
    [props.data, type, livingTypes, feePricingTypes, showLevelsCount]
  );

  const yAxis = showCareType
    ? {
        ...props.yAxis,
        label: {
          style: (text: string) => textStyle(text, props.data),
          formatter: yAxisLabelFormatter,
          autoEllipsis: true,
        },
      }
    : props.yAxis;

  const onBarClick = useCallback(
    (competitorId: number) => {
      setShowInsertDataModal(true);
      setSelectedCompetitorId(competitorId);
    },
    [setShowInsertDataModal, setSelectedCompetitorId]
  );

  useClickableAntdBars({
    chartRef,
    type,
    communityId: mainCommunity?.id,
    onBarClickCallback: onBarClick,
  });

  const barConfig = {
    yAxis,
    minBarWidth: MIN_BAR_WIDTH,
    maxBarWidth: MAX_BAR_WIDTH,
    intervalPadding: INTERVAL_PADDING,
    ...(showValueDifference && { appendPadding: [10, 90, 20, 0] }),
  };

  const expandedConfig = { ...props, ...barConfig };

  return (
    <Container>
      <Bar onReady={onReady} chartRef={chartRef} {...expandedConfig} />
      <DataInsertionBars positions={addDataBarPositions} data={props.data} />
      {showInsertDataModal && (
        <EditDataModal
          onClose={closeModal}
          open={showInsertDataModal}
          competitorId={selectedCompetitorId}
          widgetType={type}
        />
      )}
    </Container>
  );
};

export default BarChart;
